import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { BaseRiskCalculation, BaseAssessmentExchange, Step4Request, ClinicalSessionStatus } from '@/dtos/FlowClinicalSessions/index';
import { RecommendationCalculation } from '@/dtos/ResultCalculations/index';
import { useProcessStep4 } from '@/hooks/FlowClinical';
import { useUpdateReviewStatus, useUndoReview, useUpdateRecommendations } from '@/hooks/RiskCalculation';
import { usePermissions } from '@/hooks/usePermissions';
import { CheckCircle2, ArrowLeft, Loader2, TrendingUp, FileText, Plus, X, RotateCcw, GripVertical } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Step4Props {
  sessionId: string;
  onSuccess: (sessionId: string) => void;
  onBack: () => void;
  riskCalculations?: BaseRiskCalculation[];
  assessmentExchanges?: BaseAssessmentExchange[];
  isCompleted?: boolean;
}

// Risk level configuration with colors and labels
const RISK_LEVEL_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  low: { label: 'Risc Scăzut', variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
  medium: { label: 'Risc Mediu', variant: 'default', className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' },
  high: { label: 'Risc Ridicat', variant: 'default', className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
  veryhigh: { label: 'Risc Foarte Ridicat', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
};

const RECOMMENDATION_CLASS_BY_LEVEL: Record<string, string> = {
  low: 'bg-white border-border',
  medium: 'bg-white border-border',
  high: 'bg-white border-border',
  veryhigh: 'bg-white border-border',
};

// Completed status configuration
const COMPLETED_STATUS_CONFIG = {
  label: 'Finalizat',
  className: 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100',
};

// Sortable Recommendation Item Component
interface SortableRecommendationItemProps {
  id: string;
  recommendation: RecommendationCalculation;
  index: number;
  onUpdateText: (text: string) => void;
  onRemove: () => void;
  className?: string;
}

function SortableRecommendationItem({
  id,
  recommendation,
  index,
  onUpdateText,
  onRemove,
  className,
}: SortableRecommendationItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Generate code based on index
  const code = `REC_${String(index + 1).padStart(3, '0')}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 p-3 rounded-lg border hover:border-primary/50 transition-colors ${className ?? 'bg-muted border-border'}`}
    >
      <button
        {...attributes}
        {...listeners}
        className='mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors'
        aria-label='Drag to reorder'
      >
        <GripVertical className='h-5 w-5' />
      </button>
      <div className='flex-1 space-y-2'>
        <Badge variant='secondary' className='text-xs'>
          {code}
        </Badge>
        <textarea
          value={recommendation.text || ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateText(e.target.value)}
          placeholder='Text recomandare'
          rows={2}
          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none'
        />
      </div>
      <Button
        onClick={onRemove}
        variant='ghost'
        size='sm'
        className='text-destructive hover:text-destructive mt-1'
      >
        <X className='h-4 w-4' />
      </Button>
    </div>
  );
}

export default function Step4({ sessionId, onSuccess, onBack, riskCalculations: initialRiskCalculations, assessmentExchanges, isCompleted }: Step4Props) {
  const processStep4Mutation = useProcessStep4();
  const updateReviewStatusMutation = useUpdateReviewStatus();
  const undoReviewMutation = useUndoReview();
  const updateRecommendationsMutation = useUpdateRecommendations();
  const { isAdminOrMedic } = usePermissions();
  const [riskCalculations, setRiskCalculations] = useState<BaseRiskCalculation[]>(initialRiskCalculations || []);
  // Only consider processed if session is already completed
  // Step4 should always run if not completed, regardless of risk calculations
  const [hasProcessed, setHasProcessed] = useState(isCompleted);
  const processingInitiatedRef = useRef(false); // Track if we've already initiated processing
  const [editingRecommendations, setEditingRecommendations] = useState<Record<string, RecommendationCalculation[]>>({});
  const [newRecommendation, setNewRecommendation] = useState<Record<string, { code: string; text: string }>>({});

  // Update riskCalculations when prop changes (e.g., after refetch)
  useEffect(() => {
    if (initialRiskCalculations) {
      setRiskCalculations(initialRiskCalculations);
    }
  }, [initialRiskCalculations]);

  // Auto-process Step4 when component mounts if not already completed
  useEffect(() => {
    // Update hasProcessed if isCompleted changes
    if (isCompleted !== hasProcessed) {
      setHasProcessed(isCompleted);
      // Reset processing flag if step is already completed
      if (isCompleted) {
        processingInitiatedRef.current = false;
      }
    }

    // Only process if:
    // 1. Not completed
    // 2. Not already processing
    // 3. Haven't already initiated processing
    // 4. Have a valid sessionId
    if (!isCompleted && sessionId && !processStep4Mutation.isPending && !hasProcessed && !processingInitiatedRef.current) {
      console.log('[Step4] Auto-processing Step4 for session:', sessionId, 'isCompleted:', isCompleted);
      processingInitiatedRef.current = true; // Mark as initiated
      handleProcessStep4();
    } else {
      console.log('[Step4] Skipping auto-process:', { hasProcessed, isCompleted, sessionId, isPending: processStep4Mutation.isPending, processingInitiated: processingInitiatedRef.current });
    }
  }, [sessionId, isCompleted, hasProcessed, processStep4Mutation]);

  const handleProcessStep4 = async () => {
    if (!sessionId || processStep4Mutation.isPending || processingInitiatedRef.current) return;

    console.log('[Step4] Calling processStep4 API for session:', sessionId);
    processingInitiatedRef.current = true; // Mark as initiated
    const request: Step4Request = {
      sessionId: sessionId,
    };

    try {
      const response = await processStep4Mutation.mutateAsync(request);
      console.log('[Step4] Step4 response:', response);
      if (response.isSuccess && response.data) {
        console.log('[Step4] Step4 completed successfully. Progress:', response.data.progress);
        setRiskCalculations(response.data.riskCalculations || []);
        setHasProcessed(true);
        onSuccess(response.data.sessionId);
      } else {
        console.error('[Step4] Step4 failed:', response.errorMessage);
        processingInitiatedRef.current = false; // Reset on failure
      }
    } catch (error) {
      console.error('[Step4] Failed to process Step4:', error);
      processingInitiatedRef.current = false; // Reset on error
    }
  };

  const getRiskLevelKey = (riskLevel?: string) => {
    if (!riskLevel) return 'low';
    return riskLevel.toLowerCase().replace(/[\s-_]/g, '');
  };

  const getRiskLevelConfig = (riskLevel?: string) => {
    const normalized = getRiskLevelKey(riskLevel);
    return RISK_LEVEL_CONFIG[normalized] || RISK_LEVEL_CONFIG.low;
  };

  const handleApprove = async (riskCalculation: BaseRiskCalculation) => {
    // If we're in editing mode, save recommendations first
    if (editingRecommendations[riskCalculation.id]) {
      await handleSaveRecommendations(riskCalculation);
    }
    
    await updateReviewStatusMutation.mutateAsync({
      id: riskCalculation.id,
      inReview: false,
    });
    
    // Update local state - preserve recommendations
    setRiskCalculations((prev) =>
      prev.map((rc) => {
        if (rc.id === riskCalculation.id) {
          // Preserve the current recommendations from state
          const currentRecs = rc.result?.recommendations || [];
          return {
            ...rc,
            inReview: false,
            result: {
              ...rc.result,
              recommendations: currentRecs,
            },
          };
        }
        return rc;
      })
    );
  };

  const handleUndoReview = async (riskCalculation: BaseRiskCalculation) => {
    try {
      const response = await undoReviewMutation.mutateAsync(riskCalculation.id);
      
      // Update local state with response data if available
      if (response?.isSuccess && response.data) {
        const updatedData = response.data;
        setRiskCalculations((prev) =>
          prev.map((rc) => {
            if (rc.id === riskCalculation.id) {
              return {
                ...rc,
                inReview: updatedData.inReview,
                result: updatedData.result || rc.result,
              };
            }
            return rc;
          })
        );
      } else {
        // Fallback: update local state manually
        setRiskCalculations((prev) =>
          prev.map((rc) => {
            if (rc.id === riskCalculation.id) {
              return {
                ...rc,
                inReview: true,
                result: rc.result,
              };
            }
            return rc;
          })
        );
      }
    } catch (error) {
      console.error('Failed to undo review:', error);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleStartEditingRecommendations = (riskCalculation: BaseRiskCalculation) => {
    // Use current recommendations from state, or from the risk calculation prop
    const currentRecommendations = riskCalculation.result?.recommendations || [];
    // Ensure codes are generated for existing recommendations
    const recommendationsWithCodes = updateRecommendationCodes(currentRecommendations);
    setEditingRecommendations((prev) => ({
      ...prev,
      [riskCalculation.id]: recommendationsWithCodes,
    }));
    setNewRecommendation((prev) => ({
      ...prev,
      [riskCalculation.id]: { code: '', text: '' },
    }));
  };

  const handleCancelEditingRecommendations = (riskCalculationId: string) => {
    setEditingRecommendations((prev) => {
      const updated = { ...prev };
      delete updated[riskCalculationId];
      return updated;
    });
    setNewRecommendation((prev) => {
      const updated = { ...prev };
      delete updated[riskCalculationId];
      return updated;
    });
  };

  // Generate code based on index (REC_001, REC_002, etc.)
  const generateRecommendationCode = (index: number): string => {
    return `REC_${String(index + 1).padStart(3, '0')}`;
  };

  // Update recommendation codes based on their order
  const updateRecommendationCodes = (recommendations: RecommendationCalculation[]): RecommendationCalculation[] => {
    return recommendations.map((rec, index) => ({
      ...rec,
      code: generateRecommendationCode(index),
    }));
  };

  const handleAddRecommendation = (riskCalculationId: string) => {
    const newRec = newRecommendation[riskCalculationId];
    if (!newRec || !newRec.text?.trim()) return;

    const currentRecommendations = editingRecommendations[riskCalculationId] || [];
    const updatedRecommendations = [
      ...currentRecommendations,
      { code: '', text: newRec.text }, // Code will be auto-generated
    ];

    setEditingRecommendations((prev) => ({
      ...prev,
      [riskCalculationId]: updateRecommendationCodes(updatedRecommendations),
    }));
    setNewRecommendation((prev) => ({
      ...prev,
      [riskCalculationId]: { code: '', text: '' },
    }));
  };

  const handleDragEnd = (event: DragEndEvent, riskCalculationId: string) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = editingRecommendations[riskCalculationId]?.findIndex(
        (_, idx) => `rec-${idx}` === active.id
      ) ?? -1;
      const newIndex = editingRecommendations[riskCalculationId]?.findIndex(
        (_, idx) => `rec-${idx}` === over.id
      ) ?? -1;

      if (oldIndex !== -1 && newIndex !== -1) {
        const currentRecommendations = editingRecommendations[riskCalculationId] || [];
        const reordered = arrayMove(currentRecommendations, oldIndex, newIndex);
        setEditingRecommendations((prev) => ({
          ...prev,
          [riskCalculationId]: updateRecommendationCodes(reordered),
        }));
      }
    }
  };

  const handleUpdateRecommendationText = (riskCalculationId: string, index: number, text: string) => {
    setEditingRecommendations((prev) => {
      const current = prev[riskCalculationId] || [];
      const updated = [...current];
      updated[index] = { ...updated[index], text };
      return {
        ...prev,
        [riskCalculationId]: updated,
      };
    });
  };

  const handleRemoveRecommendation = (riskCalculationId: string, index: number) => {
    setEditingRecommendations((prev) => ({
      ...prev,
      [riskCalculationId]: prev[riskCalculationId]?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSaveRecommendations = async (riskCalculation: BaseRiskCalculation) => {
    const recommendations = editingRecommendations[riskCalculation.id] || [];
    // Ensure codes are up-to-date before saving
    const recommendationsWithCodes = updateRecommendationCodes(recommendations);
    
    try {
      const response = await updateRecommendationsMutation.mutateAsync({
        id: riskCalculation.id,
        recommendations: recommendationsWithCodes,
      });
      
      // Update local state with the response data if available, otherwise use our computed data
      // Response has structure: { isSuccess: boolean, data?: BaseRiskCalculation, errorMessage?: string }
      const updatedRecommendations = (response?.isSuccess && response.data?.result?.recommendations) 
        ? response.data.result.recommendations 
        : recommendationsWithCodes;
      
      setRiskCalculations((prev) =>
        prev.map((rc) => {
          if (rc.id === riskCalculation.id) {
            return {
              ...rc,
              inReview: true, // Keep inReview = true after saving recommendations
              result: {
                ...rc.result,
                recommendations: updatedRecommendations,
              },
            };
          }
          return rc;
        })
      );
      
      // Exit edit mode after saving - this will show the "Aprobă" button
      handleCancelEditingRecommendations(riskCalculation.id);
    } catch (error) {
      console.error('Failed to save recommendations:', error);
    }
  };

  const isLoading = processStep4Mutation.isPending && !hasProcessed;

  // Calculate summary statistics
  const totalAssessments = riskCalculations.length;
  const assessmentScoresByExchangeId = new Map(
    (assessmentExchanges || []).map((exchange) => [exchange.id, exchange.totalScore ?? 0])
  );

  const riskLevelTotals = riskCalculations.reduce((acc, rc) => {
    const normalized = rc.result?.riskLevel?.toLowerCase().replace(/[\s-_]/g, '') || 'low';
    const score = assessmentScoresByExchangeId.get(rc.assessmentExchangeId);
    const weight = typeof score === 'number' ? score : 1;
    acc[normalized] = (acc[normalized] || 0) + weight;
    return acc;
  }, {} as Record<string, number>);

  const riskLevelCounts = riskCalculations.reduce((acc, rc) => {
    const normalized = rc.result?.riskLevel?.toLowerCase().replace(/[\s-_]/g, '') || 'low';
    acc[normalized] = (acc[normalized] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const riskLevelOrder = ['low', 'medium', 'high', 'veryhigh'];
  const highestRiskLevel = riskLevelOrder.reduce((current, level) => {
    const currentValue = riskLevelTotals[current] ?? 0;
    const candidateValue = riskLevelTotals[level] ?? 0;
    if (candidateValue > currentValue) return level;
    if (candidateValue === currentValue) {
      return riskLevelOrder.indexOf(level) > riskLevelOrder.indexOf(current) ? level : current;
    }
    return current;
  }, 'low');

  const highestRiskConfig = getRiskLevelConfig(highestRiskLevel);

  // Check if any risk calculation is in review
  const hasInReview = riskCalculations && riskCalculations.length > 0 && riskCalculations.some(rc => rc.inReview === true);

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold'>Sumar Final</h2>
          <p className='text-muted-foreground mt-1'>Rezumatul complet al evaluării și recomandărilor</p>
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className='py-12 text-center'>
            <Loader2 className='h-12 w-12 mx-auto mb-4 animate-spin text-primary' />
            <p className='text-lg font-medium'>Se încarcă sumarul...</p>
            <p className='text-sm text-muted-foreground mt-2'>Vă rugăm să așteptați.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && riskCalculations.length === 0 && hasProcessed && (
        <Card>
          <CardContent className='py-8 text-center text-muted-foreground'>
            <FileText className='h-12 w-12 mx-auto mb-4 opacity-50' />
            <p>Nu s-au găsit calcule de risc pentru această sesiune.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && riskCalculations.length > 0 && (
        <div className='space-y-6'>
          {/* Summary Card */}
          <Card className='border-primary/20 bg-primary/5'>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <CheckCircle2 className='h-6 w-6 text-primary' />
                Rezumat Evaluare
              </CardTitle>
              <CardDescription>Statistici generale despre evaluarea completă</CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div className='p-4 bg-background rounded-lg border'>
                  <p className='text-sm text-muted-foreground'>Total Evaluări</p>
                  <p className='text-2xl font-bold mt-1'>{totalAssessments}</p>
                </div>
                <div className='p-4 bg-background rounded-lg border'>
                  <p className='text-sm text-muted-foreground'>Nivel de Risc Predominant</p>
                  <div className='mt-1'>
                    <Badge variant={highestRiskConfig.variant} className={`text-base px-3 py-1 ${highestRiskConfig.className}`}>
                      {highestRiskConfig.label}
                    </Badge>
                  </div>
                </div>
                <div className='p-4 bg-background rounded-lg border'>
                  <p className='text-sm text-muted-foreground'>Status</p>
                  <div className='mt-1'>
                    {hasInReview ? (
                      <Badge variant='outline' className='text-base px-3 py-1 bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100'>
                        <CheckCircle2 className='h-4 w-4 mr-1' />
                        În așteptare pentru aprobare
                      </Badge>
                    ) : (
                      <Badge variant='outline' className={`text-base px-3 py-1 ${COMPLETED_STATUS_CONFIG.className}`}>
                        <CheckCircle2 className='h-4 w-4 mr-1' />
                        {COMPLETED_STATUS_CONFIG.label}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Risk Level Distribution */}
              {Object.keys(riskLevelCounts).length > 0 && (
                <div className='pt-4 border-t'>
                  <p className='text-sm font-medium mb-3'>Distribuție Niveluri de Risc:</p>
                  <div className='flex flex-wrap gap-2'>
                    {Object.entries(riskLevelCounts).map(([level, count]) => {
                      const config = getRiskLevelConfig(level);
                      const totalScore = riskLevelTotals[level] ?? count;
                      return (
                        <Badge key={level} variant={config.variant} className={`px-3 py-1 ${config.className}`}>
                          {config.label}: {totalScore}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Risk Calculations Summary Cards */}
          <div className='space-y-4'>
            <h3 className='text-xl font-semibold'>Detalii Evaluări</h3>
            {riskCalculations.map((riskCalc, index) => {
              const riskLevel = riskCalc.result?.riskLevel;
              const riskLevelKey = getRiskLevelKey(riskLevel);
              const riskConfig = getRiskLevelConfig(riskLevel);
              const summary = riskCalc.result?.summary;
              const recommendations = riskCalc.result?.recommendations || [];
              const recommendationClassName =
                RECOMMENDATION_CLASS_BY_LEVEL[riskLevelKey] || 'bg-muted border-border';

              return (
                <Card key={riskCalc.id || index}>
                  <CardHeader>
                    <div className='flex items-start justify-between'>
                      <div className='flex-1'>
                        <CardTitle className='flex items-center gap-2'>
                          <TrendingUp className='h-5 w-5' />
                          {summary?.title || `Evaluare ${index + 1}`}
                        </CardTitle>
                        {summary?.description && (
                          <CardDescription className='mt-2'>{summary.description}</CardDescription>
                        )}
                      </div>
                      <Badge variant={riskConfig.variant} className={`ml-4 ${riskConfig.className}`}>
                        {riskConfig.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    {isAdminOrMedic() && editingRecommendations[riskCalc.id] ? (
                      // Editing mode - show editable, draggable recommendations
                      <div className='space-y-4'>
                        <div className='flex items-center justify-between'>
                          <p className='text-sm font-medium'>Recomandări principale:</p>
                          <div className='flex gap-2'>
                            <Button
                              onClick={() => handleSaveRecommendations(riskCalc)}
                              disabled={updateRecommendationsMutation.isPending}
                              size='sm'
                              className='bg-green-600 hover:bg-green-700'
                            >
                              {updateRecommendationsMutation.isPending ? (
                                <>
                                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                  Se salvează...
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className='h-4 w-4 mr-2' />
                                  Salvează
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleCancelEditingRecommendations(riskCalc.id)}
                              variant='outline'
                              size='sm'
                            >
                              <X className='h-4 w-4 mr-2' />
                              Anulează
                            </Button>
                          </div>
                        </div>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleDragEnd(event, riskCalc.id)}
                        >
                          <SortableContext
                            items={editingRecommendations[riskCalc.id]?.map((_, idx) => `rec-${idx}`) || []}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className='space-y-3'>
                              {editingRecommendations[riskCalc.id]?.map((rec, idx) => (
                                <SortableRecommendationItem
                                  key={`rec-${idx}`}
                                  id={`rec-${idx}`}
                                  recommendation={rec}
                                  index={idx}
                                  onUpdateText={(text) => handleUpdateRecommendationText(riskCalc.id, idx, text)}
                                  className={recommendationClassName}
                                  onRemove={() => {
                                    const updated = editingRecommendations[riskCalc.id]?.filter((_, i) => i !== idx) || [];
                                    setEditingRecommendations((prev) => ({
                                      ...prev,
                                      [riskCalc.id]: updateRecommendationCodes(updated),
                                    }));
                                  }}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                        {/* Add new recommendation */}
                        <div className='space-y-2 border-t pt-4'>
                          <p className='text-sm font-medium'>Adaugă recomandare:</p>
                          <div className='space-y-2'>
                            <textarea
                              placeholder='Text recomandare'
                              value={newRecommendation[riskCalc.id]?.text || ''}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setNewRecommendation((prev) => ({
                                  ...prev,
                                  [riskCalc.id]: {
                                    ...(prev[riskCalc.id] || { code: '', text: '' }),
                                    text: e.target.value,
                                  },
                                }))
                              }
                              rows={2}
                              className='flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                            />
                            <Button
                              onClick={() => handleAddRecommendation(riskCalc.id)}
                              variant='outline'
                              size='sm'
                              disabled={!newRecommendation[riskCalc.id]?.text?.trim()}
                            >
                              <Plus className='h-4 w-4 mr-2' />
                              Adaugă
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View mode - show recommendations
                      <>
                        <div className='space-y-2'>
                          <div className='flex items-center justify-between'>
                            <p className='text-sm font-medium'>Recomandări principale:</p>
                            {isAdminOrMedic() && (
                              <Button
                                onClick={() => handleStartEditingRecommendations(riskCalc)}
                                variant='outline'
                                size='sm'
                              >
                                <Plus className='h-4 w-4 mr-2' />
                                {recommendations.length > 0 ? 'Editează recomandări' : 'Adaugă recomandări'}
                              </Button>
                            )}
                          </div>
                          {recommendations.length > 0 ? (
                            <div className='space-y-2'>
                              {recommendations.map((rec, idx) => {
                                const code = rec.code || generateRecommendationCode(idx);
                                return (
                                  <div
                                    key={idx}
                                    className={`flex items-start gap-2 p-2 rounded-lg border ${recommendationClassName}`}
                                  >
                                    <Badge variant='outline' className={`text-xs mt-0.5 ${riskConfig.className}`}>
                                      {code}
                                    </Badge>
                                    <p className='text-sm text-muted-foreground flex-1'>{rec.text}</p>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className='p-4 bg-muted/50 rounded-lg border border-dashed text-center'>
                              <p className='text-sm text-muted-foreground'>
                                Nu există recomandări. {isAdminOrMedic() && 'Click pe "Adaugă recomandări" pentru a adăuga.'}
                              </p>
                            </div>
                          )}
                        </div>
                        {isAdminOrMedic() && (
                          <div className='flex items-center justify-end gap-2 border-t pt-4'>
                            {riskCalc.inReview ? (
                              <Button
                                onClick={() => handleApprove(riskCalc)}
                                disabled={updateReviewStatusMutation.isPending}
                                className='bg-green-600 hover:bg-green-700'
                                size='sm'
                              >
                                {updateReviewStatusMutation.isPending ? (
                                  <>
                                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                    Se procesează...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className='h-4 w-4 mr-2' />
                                    Aprobă
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleUndoReview(riskCalc)}
                                disabled={undoReviewMutation.isPending}
                                variant='outline'
                                size='sm'
                              >
                            {undoReviewMutation.isPending ? (
                              <>
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                Se procesează...
                              </>
                            ) : (
                              <>
                                <RotateCcw className='h-4 w-4 mr-2' />
                                Anulează aprobare
                              </>
                            )}
                          </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6'>
        <Button variant='outline' onClick={onBack} disabled={isLoading}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Înapoi
        </Button>
        {hasProcessed && (
          <div className='flex items-center gap-2'>
            <CheckCircle2 className='h-5 w-5 text-green-600' />
            <span className='text-sm text-muted-foreground'>Sesiunea a fost finalizată cu succes</span>
          </div>
        )}
      </div>
    </div>
  );
}
